import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../../axios";
import urls from "../../../Utils/urls";
import moment from "moment";
import { toast } from "react-toastify";
import {
  commonToast,
  groupFunction,
  imageTagToLink,
  updateLinkURLNew,
  removeSpanTag,
  reduceSum,
  isProdEnvironment,
} from "../../../Utils";
import { loadingTypes, oracleConfig, roleType } from "../../../Utils/constant";
import { getSegment } from "../../../components/DoughnutAndMeter";
import { transformLoanData } from "../../../hooks/useBorowerLoanDetail";

export const fetchKpis = createAsyncThunk(
  "dashboard/fetchKpis",
  async ({ lenderId, role, signal }) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;
      const res = await axios.get(
        urls.dashboard.getKpis(lenderId, secondParam),
        { signal },
      );
      return {
        loanCount: res?.data?.[0]?.[0]?.LoanCount || 0,
        portfolioBalance: res?.data?.[0]?.[0]?.PrincipalBalance || 0,
        upcomingPayments: res?.data?.[0]?.[0]?.TotalPaymentAmount || 0,
        outstandingCharges: res?.data?.[0]?.[0]?.TotalUnpaidBalance || 0,
        noOfDefault: 0,
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(
            `Portfolio Balance
        Upcoming Payments
        No. of Loans
        No. of Defaults`,
            {
              ...commonToast,
            },
          );
      }
      return {
        loanCount: 0,
        portfolioBalance: 0,
        upcomingPayments: 0,
        noOfDefault: 0,
      };
    }
  },
);
export const fetchPastPayments = createAsyncThunk(
  "dashboard/fetchPastPayments",
  async ({ lenderId, signal }, thunkAPI) => {
    try {
      const response = await axios.get(
        urls.dashboard.getPastPayments(lenderId, { signal }),
      );
      const responseData = response.data[0];
      const rawData = responseData?.sort(
        (a, b) => new Date(b["Check Date"]) - new Date(a["Check Date"]),
      );

      if (!rawData) {
        throw new Error("No data received");
      }

      const processedData = rawData
        ?.map((item) => {
          const accountField = item["Account"];
          if (!accountField) {
            return null;
          }

          const accountId = accountField.split(">")[1].split("<")[0];
          const loanId = accountField.split("loan/")[1].split("/")[0];

          return {
            loan_id: loanId,
            account: accountId,
            address: item[""],
            check_date: moment(item["Check Date"]).format("MM-DD-yyyy"),
            check_no: item["Check Number"]?.split("<a")[0],
            amount: item["Amount"],
            payment_code: item["Payment Code"],
            date_due: item["Date Due"]
              ? moment(item["Date Due"]).format("MM-DD-yyyy")
              : item["Date Due"],
            date_received: item["Date Received"],
            principal: item["Principal"],
            interest: item["Interest"],
            servicingFee: item["Servicing Fee"],
            lateCharge: item["Late Charge"],
            other: item["Other"],
            loanBalance: item["Loan Balance"],
          };
        })
        .filter((item) => item !== null);
      const fieldsToAggregate = [
        "amount",
        "principal",
        "interest",
        "servicingFee",
        "lateCharge",
        "other",
        "loanBalance",
      ];
      const headerSet = {
        payment_code: "-",
        date_due: "-",
      };
      return groupFunction({
        responseData: processedData,
        uniKeyToGroup: "check_no",
        fieldsToAggregate,
        headerSet,
      });
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching past payments:", error);
        !isProdEnvironment() &&
          toast.error("Past Payment Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const fetchUpcomingPayments = createAsyncThunk(
  "dashboard/fetchUpcomingPayments",
  async ({ lenderId, signal }, thunkAPI) => {
    try {
      const response = await axios.get(
        urls.dashboard.getUpcomingpayments(lenderId),
        { signal },
      );
      const rawData = response.data[0];

      if (!rawData) {
        throw new Error("No data received for upcoming payments");
      }

      const processedData = rawData?.map((item) => {
        const accountField = item["Account"];
        if (!accountField) {
          return null;
        }

        const accountId = accountField.split(">")[1].split("<")[0];
        const loanId = accountField.split("loan/")[1].split("/")[0];

        if (item["Check Number"].toLowerCase() === "immediate") {
          return {
            ...item,
            loan_id: loanId,
            account: accountId,
            "Check Date": "Today",
          };
        } else {
          return {
            ...item,
            loan_id: loanId,
            account: accountId,
            "Check Date": moment(item["Check Date"]).format("MM-DD-yyyy"),
          };
        }
      });

      const fieldsToAggregate = ["Amount"];
      const headerSet = {
        "Property Address": "-",
        "Check Number": "-",
      };

      return groupFunction({
        responseData: processedData,
        uniKeyToGroup: "Loan Account",
        fieldsToAggregate,
        headerSet,
        headerCount: "Loan Account",
      });

      // return processedData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching upcoming payments:", error);
        !isProdEnvironment() &&
          toast.error("Upcoming Payment Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchEventJournal = createAsyncThunk(
  "dashboard/fetchEventJournal",
  async ({ lenderId, signal }, thunkAPI) => {
    try {
      const response = await axios.get(
        urls.dashboard.getEventJournal(lenderId),
        { signal },
      );
      const rawData = response.data[0];

      if (!rawData) {
        throw new Error("No data received for event journal");
      }

      const processedData = rawData?.map((item) => ({
        ...item,
        event_date: moment(item["event_date"]).format("MM-DD-yyyy"),
      }));

      return processedData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching event journal:", error);
        !isProdEnvironment() &&
          toast.error("Event Journal Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchPortfolioAtGlance = createAsyncThunk(
  "dashboard/fetchPortfolioAtGlance",
  async ({ lenderId, role, signal }, thunkAPI) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;

      // const response = await axios.get(
      //   urls.dashboard.getPortfolioOverview(lenderId, secondParam),
      //   { signal }
      // );
      const response = await axios.get(
        urls.dashboard.getPortfolioOverviewNew(lenderId, secondParam),
        { signal },
      );
      let responseData = JSON.parse(JSON.stringify(response.data[0]));
      const rawData = responseData?.sort(
        (a, b) => new Date(b.next_due_date) - new Date(a.next_due_date),
      );

      if (!rawData) {
        throw new Error("No data received for portfolio at glance");
      }

      const processedData = rawData
        .filter((item) => !!item.account)
        ?.map((item) => {
          const accountSplit = item.account.split("loan/")[1].split("/");
          const accountId = accountSplit[0];
          const formattedAccount = item.account.split(">")[1].split("<")[0];

          return {
            ...item,
            "Check Date": moment(item["Check Date"]).format("MM-DD-yyyy"),
            next_due_date: item["next_due_date"]?.split("/")?.join("-"),
            maturity_date: moment(item["maturity_date"], "MM-DD-YYYY").format(
              "MM-DD-yyyy",
            ),
            loan_id: accountId,
            account: formattedAccount,
            loan_info: item["loan_info"]
              ?.replace("<br>", "")
              .replace(",,", ","),
            insurance_info: imageTagToLink(item.insurance_info), // Assuming imageTagToLink is a defined function
          };
        });

      return processedData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching portfolio at glance:", error);
        !isProdEnvironment() &&
          toast.error("Portfolio At Glance Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const fetchPortfolioAtGlanceBroker = createAsyncThunk(
  "dashboard/fetchPortfolioAtGlanceBroker",
  async ({ lenderId, role, signal }, thunkAPI) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;

      const response = await axios.get(
        urls.dashboard.getPortfolioOverviewBroker(lenderId, secondParam),
        { signal },
      );
      // const response = await axios.get(
      //   urls.dashboard.getPortfolioOverviewBrokerNew(lenderId, secondParam),
      //   { signal }
      // );

      let rawData = JSON.parse(JSON.stringify(response.data[0]));

      if (!rawData) {
        throw new Error("No data received for portfolio at glance");
      }

      const processedData = rawData
        .filter((item) => !!item.account)
        ?.map((item) => {
          const accountSplit = item.account.split("loan/")[1].split("/");
          const accountId = accountSplit[0];
          const formattedAccount = item.account.split(">")[1].split("<")[0];

          return {
            ...item,
            "Check Date": moment(item["Check Date"]).format("MM-DD-yyyy"),
            next_due_date: item["next_due_date"]?.split("/")?.join("-"),
            maturity_date: moment(item["maturity_date"], "MM-DD-YYYY").format(
              "MM-DD-yyyy",
            ),
            loan_id: accountId,
            account: formattedAccount,
            loan_info: item["loan_info"]
              ?.replace("<br>", "")
              .replace(",,", ","),
            insurance_info: imageTagToLink(item.insurance_info), // Assuming imageTagToLink is a defined function
          };
        });

      return processedData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error("Error fetching portfolio at glance:", error);
        !isProdEnvironment() &&
          toast.error("Portfolio At Glance Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchPortfolioAtGlanceBrokerNew = createAsyncThunk(
  "dashboard/fetchPortfolioAtGlanceBrokerNew",
  async ({ lenderId, role, signal }, thunkAPI) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;

      const response = await axios.post(
        urls.dashboard.getPortfolioOverviewBrokerNew(lenderId, secondParam),
        {
          Filter: "",
          Value: "",
        },
        { signal },
      );

      let rawData = JSON.parse(JSON.stringify(response.data[0]));

      if (!rawData) {
        throw new Error("No data received for portfolio at glance");
      }

      const processedData = rawData
        .filter((item) => !!item.account)
        ?.map((item) => {
          const accountSplit = item.account.split("loan/")[1].split("/");
          const accountId = accountSplit[0];
          const formattedAccount = item.account.split(">")[1].split("<")[0];

          return {
            ...item,
            "Check Date": moment(item["Check Date"]).format("MM-DD-yyyy"),
            next_due_date: item["next_due_date"]?.split("/")?.join("-"),
            maturity_date: moment(item["maturity_date"], "MM-DD-YYYY").format(
              "MM-DD-yyyy",
            ),
            loan_id: accountId,
            account: formattedAccount,
            loan_info: item["loan_info"]
              ?.replace("<br>", "")
              .replace(",,", ","),
            insurance_info: imageTagToLink(item.insurance_info), // Assuming imageTagToLink is a defined function
          };
        });

      return processedData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error("Error fetching portfolio at glance:", error);
        !isProdEnvironment() &&
          toast.error("Portfolio At Glance Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchPAGWithFilters = createAsyncThunk(
  "dashboard/fetchPAGWithFilters",
  async ({ lenderId, extraparm, role }, thunkAPI) => {
    try {
      // Simplify the determination of brokerLenderExist and updatedLenderId.
      const brokerLenderExist = Boolean(extraparm?.brokerLenderId);
      const updatedLenderId = brokerLenderExist
        ? extraparm.brokerLenderId
        : lenderId;
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;

      // Prepare the request body outside of the axios call for clarity.
      const requestBody = {
        Filter: brokerLenderExist ? "" : extraparm.filterKey,
        Value: brokerLenderExist ? "" : extraparm.value,
        is_broker: brokerLenderExist ? 0 : secondParam,
        lenderID: lenderId ? lenderId : "",
        brokerID:
          role === roleType.broker || role === roleType.brokerAdmin
            ? lenderId
            : "0",
      };

      // DRY the axios call by abstracting repetitive setup.
      const response = await axios.post(
        urls.dashboard.getPortfolioOverviewFiltered(updatedLenderId),
        requestBody,
        { headers: { "Content-Type": "application/json" } },
      );

      // Utilize optional chaining and nullish coalescing operator to simplify.
      const rawData = response.data?.[0] ?? [];

      // Extract the mapping logic to a separate function for better readability.
      const processItem = (item) => {
        // eslint-disable-next-line no-unused-vars
        const [_, loanId, account] =
          item.account.match(/loan\/([^\s/]+)\/.*>([^<]+)</) || [];
        return {
          ...item,
          "Check Date": moment(item["Check Date"]).format("MM-DD-yyyy"),
          next_due_date: item["next_due_date"]?.replace(/\//g, "-"),
          maturity_date: moment(item["maturity_date"], "MM-DD-YYYY").format(
            "MM-DD-yyyy",
          ),
          loan_id: loanId,
          account: account,
          loan_info: item["loan_info"]?.replace("<br>", "").replace(",,", ","),
          insurance_info: imageTagToLink(item.insurance_info),
        };
      };

      // Map and filter in a more concise manner.
      const processedData = rawData
        .filter((item) => !!item.account)
        .map(processItem);

      return processedData;
    } catch (error) {
      console.error("Error fetching portfolio at glance:", error);
      !isProdEnvironment() &&
        toast.error(`${extraparm.title}: ${extraparm.value} Failed`, {
          ...commonToast,
        });
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const fetchLoanAging = createAsyncThunk(
  "dashboard/fetchLoanAging",
  async ({ lenderId, role, signal }) => {
    const secondParam =
      role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;

    function calculateLoanCounts(data) {
      const categories = ["Current", "0-30", "31-60", "61-90", "91+"];
      return categories?.map((category) => ({
        category: category.toLowerCase(),
        value: data?.filter((item) => item["days late range"] === category)
          ?.length,
      }));
    }

    function getDefaultResponse() {
      return {
        valuesToPopulate: calculateLoanCounts([]),
        loanAgingTable: [],
        summary: {},
      };
    }
    try {
      const res = await axios.get(
        urls.dashboard.getLoanAging(lenderId, secondParam),
        { signal },
      );
      let data = res?.data?.[0] || [];
      // data.shift(); // Remove the first summary item

      const loanAgingTable = data?.map((item) => {
        const paymentAmount =
          parseFloat(item?.["Payment Amount"]?.replace(",", "")) || 0;
        const principalBalance = parseFloat(item?.["Principal Balance"]) || 0;
        const daysLateRange = item?.["days late range"];
        // Process individual loan item
        return {
          ...item,
          primaryBorrower: item?.FullName,
          principalBalance: principalBalance,
          zeroToThirty: daysLateRange === "0-30" ? paymentAmount : "",
          tillSixty: daysLateRange === "31-60" ? paymentAmount : "",
          tillNinety: daysLateRange === "61-90" ? paymentAmount : "",
          NinetyOnePlus: daysLateRange === "91+" ? paymentAmount : "",
          current: daysLateRange === "Current" ? paymentAmount : "",
        };
      });

      // Format summary

      // Calculate loan counts
      const valuesToPopulate = calculateLoanCounts(data);

      return { valuesToPopulate, loanAgingTable };
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error("Loan Aging Error", error);
        !isProdEnvironment() && toast.error("Loan Aging Error", commonToast);
      }
      return getDefaultResponse();
    }
  },
);
export const fetchOldLoanPortfolio = createAsyncThunk(
  "dashboard/fetchOldLoanPortfolio",
  async ({ lenderId, role, signal }, thunkAPI) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;
      const res = await axios.get(
        urls.dashboard.getOldLoanPortfolio(lenderId, secondParam),
        { signal },
      );

      let responseData = res?.data?.[0] || [];
      const data = responseData?.sort(
        (a, b) => new Date(b.payoff_date) - new Date(a.payoff_date),
      );

      const finalData = data?.map((item) => {
        item["Check Date"] = moment(new Date(item["Check Date"])).format(
          "MM-DD-yyyy",
        );
        // item["next_due_date"] = moment(
        //   // item["Next Due Dates"],
        //   item["next_due_date"],
        //   "DD/MM/YYYY"
        // ).format("MM-DD-yyyy");
        item["maturity_date"] = item["maturity_date"]?.split("/").join("-");
        item["payoff_date"] = item["payoff_date"]?.split("/").join("-");
        item["loan_id"] = item["account"].split("loan/")[1].split("/")[0];
        let acc = JSON.parse(JSON.stringify(item["account"]));
        item["account"] = acc.split(">")[1].split("<")[0];
        item["loan_info"] = item["loan_info"]
          .replace("<br>", "")
          .replace(",,", ",");
        return item;
      });

      return finalData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Past Loan Portfolio Failed`, {
            ...commonToast,
          });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const fetchLoanCharges = createAsyncThunk(
  "dashboard/fetchLoanCharges",
  async ({ lenderId, signal }) => {
    try {
      const res = await axios.get(
        urls.dashboard.getLenderLoanCharges(lenderId),
        { signal },
      );
      const responseData = res?.data?.[0] || [];
      const data = responseData?.sort(
        (a, b) => new Date(b.Date) - new Date(a.Date),
      );
      return data.map((inner) => ({
        ...inner,
        "Owed to Account": inner["Owed to Account"]?.replace(
          /<br\s*\/?>/gi,
          " ",
        ),
        "On Behalf Of": inner["On Behalf Of"]
          ? inner["On Behalf Of"]?.replace(/<br\s*\/?>/gi, " ")
          : inner["On Behalf Of"],
        Deferred: inner.Deferred === "" ? "No" : inner.Deferred,
      }));
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Loan Charges Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchLAdditionalInterestSkim = createAsyncThunk(
  "dashboard/fetchLAdditionalInterestSkim",
  async ({ lenderId, role, signal }) => {
    try {
      const secondParam = 1;
      // role === roleType.broker || role === roleType.brokerAdmin ? 0 : 1;
      const res = await axios.get(
        urls.broker.getAdditionalInterestSkim(lenderId, secondParam),
        { signal },
      );
      const data = res?.data?.[0] || [];
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Additional InterestSkim Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchAttachments = createAsyncThunk(
  "dashboard/fetchAttachments",
  async ({ lenderId, signal }) => {
    try {
      const res = await axios.get(urls.broker.getAttachments(lenderId), {
        signal,
      });
      let data = res?.data?.[0] || [];
      const updatedData = data?.map((item) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(item.attachment, "text/html");
        const link = doc.querySelector("a");

        if (link) {
          const titleValue = removeSpanTag(link.innerHTML);
          const hrefValue = updateLinkURLNew(link.getAttribute("href"));
          return {
            Date: item.date,
            Attachment: {
              hrefValue,
              titleValue,
            },
          };
        }

        return item;
      });
      return updatedData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Attachments Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchAttachmentsLender = createAsyncThunk(
  "dashboard/fetchAttachments",
  async ({ lenderId, signal }) => {
    try {
      const res = await axios.get(urls.lender.getAttachmentsLender(lenderId), {
        signal,
      });
      let data = res?.data?.[0] || [];
      const updatedData = data?.map((item) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(item.attachment, "text/html");
        const link = doc.querySelector("a");

        if (link) {
          const titleValue = removeSpanTag(link.innerHTML);
          const hrefValue = updateLinkURLNew(link.getAttribute("href"));
          return {
            Date: item.date,
            Attachment: {
              hrefValue,
              titleValue,
            },
          };
        }

        return item;
      });
      return updatedData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Attachments Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchRevYTDPie = createAsyncThunk(
  "dashboard/fetchRevYTDPie",
  async ({ lenderId, extraParam, role, signal }) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;
      const res = await axios.get(
        urls.dashboard.getRevYTDPie(lenderId, extraParam, secondParam),
        { signal },
      );
      const data = res?.data?.[0] || [];
      const output = () => {
        const category = {
          Interest: 0,
          "Servicing Fees": 0,
          "Late Charges": 0,
          Prepays: 0,
          "Other Fees": 0,
        };

        // Calculate category sums
        data?.forEach((month) => {
          category.Interest += month.SumOfToInterest;
          category["Servicing Fees"] += month.SumOfToServiceFee;
          category["Late Charges"] += month.SumOfToLateCharge;
          category.Prepays += month.SumOfToPrepay;
          category["Other Fees"] += month.SumOfToOther;
        });

        // Calculate total amount
        const totalAmount = Object.values(category).reduce(
          (acc, amount) => acc + amount,
          0,
        );

        // Create response with percentages
        const resp = Object.keys(category)?.map((categoryName) => {
          const amount = category[categoryName];
          const percent =
            totalAmount > 0
              ? Number(((amount / totalAmount) * 100).toFixed(2))
              : 0;
          return { categoryName, amount, percent };
        });

        return {
          resp,
          total: {
            categoryName: "",
            amount: totalAmount,
            percent: reduceSum(resp?.map((data) => data.percent)).toFixed(2),
          },
        };
      };
      return output();
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Review Year to Date Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchLoanDistByPriorityPie = createAsyncThunk(
  "dashboard/fetchLoanDistByPriorityPie",
  async ({ lenderId, role, signal }) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;
      const res = await axios.get(
        urls.dashboard.getLoanDistByPriorityPie(lenderId, secondParam),
        { signal },
      );
      const data = res?.data?.[0] || [];
      function summarizePriorityData(data) {
        // Validate input data
        if (!Array.isArray(data) || data?.length === 0) {
          return {
            resp: [],
            total: {
              priority: "",
              amount: 0,
              count: 0,
              percent: 100,
            },
          };
        }

        // Helper function to format priority
        const formatPriority = (priority) => {
          const priorityMapping = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th" };
          return priorityMapping[priority] || "Other";
        };

        // Summarize data
        const summary = data.reduce((acc, { Priority, PrinBal }) => {
          if (typeof Priority !== "number" || typeof PrinBal !== "number") {
            return acc; // Skip invalid entries
          }

          const formattedPriority = formatPriority(Priority);
          if (!acc[formattedPriority]) {
            acc[formattedPriority] = { Amount: 0, Count: 0 };
          }
          acc[formattedPriority].Amount += PrinBal;
          acc[formattedPriority].Count += 1;
          return acc;
        }, {});

        // Calculate percentage
        const totalCount = data?.length;
        Object.keys(summary)?.forEach((priority) => {
          summary[priority].Percent = Number(
            ((summary[priority].Count / totalCount) * 100).toFixed(2),
          );
        });

        // Convert summary to array
        const resp = Object.entries(summary)?.map(
          ([priority, { Amount, Count, Percent }]) => ({
            priority,
            amount: Number(Amount.toFixed(2)),
            count: Count,
            percent: Percent,
          }),
        );
        return {
          resp,
          total: {
            priority: "",
            amount: reduceSum(resp?.map((data) => data.amount)),
            count: reduceSum(resp?.map((data) => data.count)),
            percent: 100,
          },
        };
      }

      return summarizePriorityData(data);
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Loan Distribution by Priority Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchLoanDistByMaturityPie = createAsyncThunk(
  "dashboard/fetchLoanDistByMaturityPie",
  async ({ lenderId, role, signal }) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;
      const res = await axios.get(
        urls.dashboard.getLoanDistByMaturityPie(lenderId, secondParam),
        { signal },
      );
      const data = res?.data?.[0] || [];
      const totalSumOfPrinBal = data.reduce(
        (acc, { SumOfPrinBal }) => acc + SumOfPrinBal,
        0,
      );
      const resp = data?.map((item) => {
        const { Total, SumOfPrinBal, Year } = item;
        return {
          year: Year,
          count: Total,
          amount: SumOfPrinBal,
          percent:
            totalSumOfPrinBal > 0
              ? Number(((SumOfPrinBal / totalSumOfPrinBal) * 100).toFixed(2))
              : 0,
        };
      });
      return {
        resp,
        total: {
          year: "",
          amount: reduceSum(resp?.map((data) => data.amount)),
          count: reduceSum(resp?.map((data) => data.count)),
          percent: 100,
        },
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Loan Distribution by Maturity Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchLoanAgingPie = createAsyncThunk(
  "dashboard/fetchLoanAgingPie",
  async ({ lenderId, role, signal }) => {
    const secondParam =
      role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;
    try {
      const res = await axios.get(
        urls.dashboard.getLoanAgingPie(lenderId, secondParam),
        { signal },
      );
      const data = res?.data?.[0] || [];
      const totalAmount = data.reduce(
        (acc, { PrincipalBalance }) => acc + PrincipalBalance,
        0,
      );

      const output = () => {
        const categories = {
          Current: { count: 0, amount: 0 },
          "1-30": { count: 0, amount: 0 },
          "31-60": { count: 0, amount: 0 },
          "61-90": { count: 0, amount: 0 },
          "91-120": { count: 0, amount: 0 },
          "121-150": { count: 0, amount: 0 },
          "over 150 days": { count: 0, amount: 0 },
        };

        data?.forEach((loan) => {
          const daysOverdue = loan["Days Overdue"];
          const dayCount = loan["LoanCount"] || 0; // Use DayCount from the loan data or default to 0
          const principalBalance = loan["PrincipalBalance"] || 0; // Use PrincipalBalance from the loan data or default to 0

          // Determine the category based on the 'Days Overdue'
          let category;
          if (daysOverdue === "Current") {
            category = "Current";
          } else if (daysOverdue === "1-30") {
            category = "1-30";
          } else if (daysOverdue === "31-60") {
            category = "31-60";
          } else if (daysOverdue === "61-90") {
            category = "61-90";
          } else if (daysOverdue === "91-120") {
            category = "91-120";
          } else if (daysOverdue === "121-150") {
            category = "121-150";
          } else {
            category = "over 150 days";
          }

          // Increment the count and add the PrincipalBalance to the amount for the corresponding category
          categories[category].count += dayCount;
          categories[category].amount += principalBalance;
        });

        // Transform the result into an array of objects
        return Object.keys(categories)?.map((categoryName) => {
          return {
            categoryName,
            count: categories[categoryName].count,
            amount: categories[categoryName].amount,
            percent:
              totalAmount > 0
                ? Number(
                    (
                      (categories[categoryName].amount / totalAmount) *
                      100
                    ).toFixed(2),
                  )
                : 0,
          };
        });
      };
      const resp = output();
      return {
        resp,
        total: {
          categoryName: "",
          amount: reduceSum(resp?.map((data) => data.amount)),
          count: reduceSum(resp?.map((data) => data.count)),
          percent: reduceSum(resp?.map((data) => data.percent)).toFixed(2),
        },
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Loan Aging Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchLoanDistByPropTypePie = createAsyncThunk(
  "dashboard/fetchLoanDistByPropTypePie",
  async ({ lenderId, role, signal }) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;
      const res = await axios.get(
        urls.dashboard.getLoanDistByPropTypePie(lenderId, secondParam),
        { signal },
      );
      const data = res?.data?.[0] || [];
      function summarizePropertyData(data) {
        // Calculate the total SumOfPrinBal
        const totalSumOfPrinBal = data.reduce(
          (acc, { SumOfPrinBal }) => acc + SumOfPrinBal,
          0,
        );

        // Map each entry to the new structure and calculate the percent
        const resp = data?.map(({ PropertyType, SumOfPrinBal, Total }) => ({
          propertyType: PropertyType || "Unknown", // Handle empty PropertyType
          amount: SumOfPrinBal,
          count: Total,
          percent:
            totalSumOfPrinBal > 0
              ? Number(((SumOfPrinBal / totalSumOfPrinBal) * 100).toFixed(2))
              : 0,
        }));
        return {
          resp,
          total: {
            propertyType: "",
            amount: reduceSum(resp?.map((data) => data.amount)),
            count: reduceSum(resp?.map((data) => data.count)),
            percent: 100,
          },
        };
      }
      return summarizePropertyData(data);
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Loan Distribution by Primary Property Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchWLTVByPropTypePie = createAsyncThunk(
  "dashboard/fetchWLTVByPropTypePie",
  async ({ lenderId, role }) => {
    try {
      const secondParam =
        role === roleType.broker || role === roleType.brokerAdmin ? 1 : 0;
      const res = await axios.get(
        urls.dashboard.getWLTVByPropTypePie(lenderId, secondParam),
      );
      const data = res?.data?.[0] || [];
      function summarizePropertyData(data) {
        const resp = data?.map(({ PropertyType, SumOfPrinBal }) => ({
          propertyType: PropertyType || "Unknown", // Handle empty PropertyType
          amount: SumOfPrinBal,
        }));
        return {
          resp,
          total: {
            propertyType: "",
            amount: reduceSum(resp?.map((data) => data.amount)),
          },
        };
      }
      return summarizePropertyData(data);
    } catch (error) {
      console.error(error);
      !isProdEnvironment() &&
        toast.error(`Weighted LTV by Primary Property Type Failed`, {
          ...commonToast,
        });
      return [];
    }
  },
);
export const fetchLoanDistPrinBalPie = createAsyncThunk(
  "dashboard/fetchLoanDistPrinBalPie",
  async ({ lenderId, signal }) => {
    try {
      const res = await axios.get(
        urls.dashboard.getLoanDistPrinBalPie(lenderId),
        { signal },
      );
      const data = res?.data?.[0] || [];
      function summarizePropertyData(data) {
        const resp = data?.map(({ SumOfPrinBal, ...innerData }) => ({
          amount: SumOfPrinBal,
          ...innerData,
        }));
        return {
          resp,
          total: {
            propertyType: "",
            amount: reduceSum(resp?.map((data) => data.amount)),
          },
        };
      }
      return summarizePropertyData(data);
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Weighted LTV by Primary Property Type Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);
export const fetchActiveLoanSnapShot = createAsyncThunk(
  "dashboard/fetchActiveLoanSnapShot",
  async ({ lenderId, role, signal }) => {
    try {
      const res = await axios.get(urls.broker.getActiveLoanSnapShot(lenderId), {
        signal,
      });
      let responseData = res?.data?.[0] || [];
      const data = responseData?.sort(
        (a, b) => new Date(b.next_due_date) - new Date(a.next_due_date),
      );
      if (data) {
        const updatedData = data?.map((data2) => {
          const accountSplit = data2.account.split("loan/")[1].split("/");
          const accountId = accountSplit[0];
          const formattedAccount = data2.account.split(">")[1].split("<")[0];
          return {
            ...data2,
            insurance_info: imageTagToLink(data2.insurance_info),
            loan_id: accountId,
            account: formattedAccount,
          };
        });
        return updatedData;
      } else {
        return [];
      }
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Active Loan SnapShot Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);

export const fetchBorrowerTotalLoanDetails = createAsyncThunk(
  "dashboard/fetchBorrowerTotalLoanDetails",
  async ({ requestBody }, thunkAPI) => {
    try {
      const res = await axios.post(
        urls.borrower.getDashboardDetails2,
        requestBody,
        { headers: { "Content-Type": "application/json" } },
      );
      const data = res?.data?.[0] || [];
      return data;
    } catch (error) {
      console.error(error);
      !isProdEnvironment() &&
        toast.error("Total loan values api failed", { ...commonToast });
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerDashboardPayOffDetails = createAsyncThunk(
  "dashboard/fetchBorrowerDashboardPayOffDetails",
  async ({ loanId, signal }) => {
    try {
      const res = await axios.get(
        urls.borrower.getBorrowerPayoffDetails(loanId),
        oracleConfig,
        { signal },
      );

      const data = res?.data?.items || [];

      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`PayOff details api failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);

export const fetchBorrowerLoanAging = createAsyncThunk(
  "dashboard/fetchBorrowerLoanAging",
  async ({ requestedData, signal }) => {
    try {
      const res = await axios.post(
        urls.borrower.borrowerLoanAging,
        requestedData,
        {
          signal,
        },
      );

      let data = res?.data?.[0] || [];

      const totalAmount = data.reduce(
        (acc, { PrincipalBalance }) => acc + PrincipalBalance,
        0,
      );

      const output = () => {
        const categories = {
          Current: { count: 0, amount: 0 },
          "1-30": { count: 0, amount: 0 },
          "31-60": { count: 0, amount: 0 },
          "61-90": { count: 0, amount: 0 },
          "91-120": { count: 0, amount: 0 },
          "121-150": { count: 0, amount: 0 },
          "over 150 days": { count: 0, amount: 0 },
        };

        data?.forEach((loan) => {
          const daysOverdue = loan["Days Overdue"];
          const dayCount = loan["LoanCount"] || 0; // Use DayCount from the loan data or default to 0
          const principalBalance = loan["PrincipalBalance"] || 0; // Use PrincipalBalance from the loan data or default to 0

          // Determine the category based on the 'Days Overdue'
          let category;
          if (daysOverdue === "Current") {
            category = "Current";
          } else if (daysOverdue === "1-30") {
            category = "1-30";
          } else if (daysOverdue === "31-60") {
            category = "31-60";
          } else if (daysOverdue === "61-90") {
            category = "61-90";
          } else if (daysOverdue === "91-120") {
            category = "91-120";
          } else if (daysOverdue === "121-150") {
            category = "121-150";
          } else {
            category = "over 150 days";
          }

          // Increment the count and add the PrincipalBalance to the amount for the corresponding category
          categories[category].count += dayCount;
          categories[category].amount += principalBalance;
        });

        // Transform the result into an array of objects
        return Object.keys(categories)?.map((categoryName) => {
          return {
            categoryName,
            count: categories[categoryName].count,
            amount: categories[categoryName].amount,
            percent:
              totalAmount > 0
                ? Number(
                    (
                      (categories[categoryName].amount / totalAmount) *
                      100
                    ).toFixed(2),
                  )
                : 0,
          };
        });
      };
      const resp = output();
      return {
        resp,
        total: {
          categoryName: "",
          amount: reduceSum(resp?.map((data) => data.amount)),
          count: reduceSum(resp?.map((data) => data.count)),
          percent: reduceSum(resp?.map((data) => data.percent)).toFixed(2),
        },
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Loan aging failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);

export const fetchBorrowerInsuranceExpiry = createAsyncThunk(
  "dashboard/fetchBorrowerInsuranceExpiry",
  async ({ requestedData, signal, activeAccounts }) => {
    try {
      const res = await axios.post(
        urls.borrower.borrowerInsuranceExpiry,
        requestedData,
        {
          signal,
        },
      );

      let data = res?.data?.[0] || [];
      let formData,
        filteredInsuredData = [];

      if (data?.length) {
        const accountValues = activeAccounts.map(({ account }) =>
          account.trim().toLowerCase(),
        );

        const filteredInsuranceData = data?.filter(({ Account }) =>
          accountValues.includes(Account?.trim()?.toLowerCase()),
        );
        const uniqueInsuranceFilterdArr = filteredInsuranceData.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.Account === item.Account),
        );
        const thirtyDaysMoment = moment().add(30, "days");

        if (filteredInsuranceData.length) {
          filteredInsuredData = filteredInsuranceData;

          const finalPrincipal = reduceSum(
            uniqueInsuranceFilterdArr.map((item) => item?.["OrigBal"]),
          );
          const insuranceExpireIn30DaysArr = filteredInsuranceData
            .filter(({ InsExpiration }) =>
              // moment(InsExpiration).isBetween(
              //   moment(),
              //   thirtyDaysMoment,
              //   null,
              //   "[]"
              // )
              moment(InsExpiration).isSameOrBefore(thirtyDaysMoment, "day"),
            )
            .filter(
              (item, index, self) =>
                index === self.findIndex((t) => t.Account === item.Account),
            );
          const sumOfInsExpirationWithin30Days = reduceSum(
            insuranceExpireIn30DaysArr.map((item) => item?.["OrigBal"]),
          );
          const riskFactor =
            Number(
              (sumOfInsExpirationWithin30Days / finalPrincipal).toFixed(2),
            ) * 100;

          formData = {
            finalPrincipal: sumOfInsExpirationWithin30Days,
            riskFactor,
            segment:
              sumOfInsExpirationWithin30Days === 0
                ? 100
                : getSegment(riskFactor),
            LoanCount: activeAccounts.length,
            loanInsured: uniqueInsuranceFilterdArr.length || 0,
            insExpirationWithin30Days: insuranceExpireIn30DaysArr.length,
            status: loadingTypes.fulfilled,
          };
        } else {
          formData = {
            ...formData,
            // segment: 100,
            segment: 0,
            loanInsured: uniqueInsuranceFilterdArr.length || 0,
            LoanCount: activeAccounts.length,
            status: loadingTypes.fulfilled,
          };
        }
      } else {
        formData = {
          ...formData,
          segment: 100,
          LoanCount: activeAccounts.length,
          status: "No data",
        };
      }

      return { formData, filteredInsuredData };
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        // console.error(error);
        console.error(error);
        !isProdEnvironment() &&
          toast.error(`Borrower Insurance Expiry Failed`, {
            ...commonToast,
          });
      }
      return [];
    }
  },
);

export const fetchBorrowerPortfolioLoans = createAsyncThunk(
  "dashboard/fetchBorrowerPortfolioLoans",
  async ({ requestBody, signal }, thunkAPI) => {
    try {
      const res = await axios.post(
        urls.borrower.borrowerPortfolioLoans,
        requestBody,
        { signal },
      );
      let rawData = JSON.parse(JSON.stringify(res.data[0]));

      if (!rawData) {
        throw new Error("No data received for portfolio at glance");
      }

      const processedData = rawData
        .filter((item) => !!item.account)
        ?.map((item) => {
          const accountSplit = item.account.split("loan/")[1].split("/");
          const accountId = accountSplit[0];
          const formattedAccount = item.account.split(">")[1].split("<")[0];

          return {
            ...item,
            "Check Date": moment(item["Check Date"]).format("MM-DD-yyyy"),
            next_due_date: item["next_due_date"]?.split("/")?.join("-"),
            maturity_date: moment(item["maturity_date"], "MM-DD-YYYY").format(
              "MM-DD-yyyy",
            ),
            loan_id: accountId,
            account: formattedAccount,
            loan_info: item["loan_info"]
              ?.replace("<br>", "")
              .replace(",,", ","),
            insurance_info: imageTagToLink(item.insurance_info), // Assuming imageTagToLink is a defined function
          };
        });

      return processedData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching portfolio at glance:", error);
        !isProdEnvironment() &&
          toast.error("Portfolio At Glance Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerDefaultLoans = createAsyncThunk(
  "dashboard/fetchBorrowerDefault",
  async ({ requestBody, signal }, thunkAPI) => {
    try {
      const response = await axios.post(
        urls.borrower.borrowerDefaultLoans,
        requestBody,
        { signal },
      );

      const res = response.data[0]?.map((item) => {
        const accountSplit = item["Loan Account"].split("loan/")[1].split("/");
        const accountId = accountSplit[0];
        const formattedAccount = item["Loan Account"]
          .split(">")[1]
          .split("<")[0];
        return item["Loan Account"]
          ? { ...item, loan_id: accountId, account: formattedAccount }
          : null;
      });

      const loansInForeclosure = res.filter((item) => item.SRV_FCStatus === 1);
      const loansInWorkout = res.filter((item) => item.SRV_WorkoutStatus === 1);
      const loansInBankruptcy = res.filter(
        (item) => item.SRV_BankruptcyStatus === 1,
      );
      return {
        loansInBankruptcy,
        loansInForeclosure,
        loansInWorkout,
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching portfolio at glance:", error);
        !isProdEnvironment() &&
          toast.error("Default loans api Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerPastDue31Days = createAsyncThunk(
  "dashboard/fetchBorrowerPastDue31Days",
  async ({ requestBody, signal }, thunkAPI) => {
    try {
      const response = await axios.post(
        urls.borrower.borrowerLoansPastDue31Days,
        requestBody,
        signal,
      );

      return response.data[0]?.map((data) => ({
        ...data,
        loan_id: data?.LoanRecID,
        days_late: data?.["days late"],
      }));
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching portfolio at glance:", error);
        !isProdEnvironment() &&
          toast.error(" Loans Past due 31 days api Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerAllLoansBankDetails = createAsyncThunk(
  "dashboard/fetchBorrowerAllLoansBankDetails",
  // async ({ accountIds, userId, signal }, thunkAPI) => {
  async ({ requestBody, userId, signal }, thunkAPI) => {
    try {
      const response = await axios.post(
        urls.borrower.getBankingDetailsForAll(userId),
        requestBody,
        oracleConfig,
      );

      const data = response?.data?.accounts || [];
      // const response = await Promise.all(
      //   accountIds.map((id) =>
      //     axios.get(urls.borrower.getBankingDetails(userId, id), {
      //       signal,
      //     })
      //   )
      // );

      // if (response?.length) {
      // const data = response.map((res) => res?.data?.data?.items) || [];

      const options = data?.map((el) => ({
        ...el,
        label: el.account_name,
        value: el.NICK_NAME,
      }));

      const finalData = {
        data: data,
        options: options,
      };
      return finalData;
      // }
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching portfolio at glance:", error);
        !isProdEnvironment() &&
          toast.error("Bank Details api Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerAllAccountsLoanDetails = createAsyncThunk(
  "dashboard/fetchBorrowerAllAccountsLoanDetails",
  async ({ accountIds, signal }, thunkAPI) => {
    try {
      const responses = await Promise.all(
        accountIds.map((id) => axios.get(urls.dashboard.getLoanTerm2(id))),
      );
      const allData = responses.map(
        (res) => transformLoanData(res.data?.[0]?.[0]) || null,
      );

      return allData;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching portfolio at glance:", error);
        !isProdEnvironment() &&
          toast.error("Loan accounts details api Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerMaturingLoans = createAsyncThunk(
  "dashboard/fetchBorrowerMaturingLoans",
  async ({ requestedBody, headers, signal }, thunkAPI) => {
    try {
      const res = await axios.post(
        urls.borrower.maturingLoans,
        requestedBody,
        headers,
      );

      const data = res?.data?.loans;
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching maturing loans:", error);
        !isProdEnvironment() &&
          toast.error("Loans Maturing api Failed", { ...commonToast });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerInsuranceExpiringWithIn30Days = createAsyncThunk(
  "dashboard/fetchBorrowerInsuranceExpiringWithIn30Days",
  async ({ requestedBody, headers }, thunkAPI) => {
    try {
      const res = await axios.post(
        urls.borrower.insuranceExpiryLoans,
        requestedBody,
        headers,
      );

      const data = res?.data?.insurance;
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error(
          "Error fetching insurance expiring with in 30 days:",
          error,
        );
        !isProdEnvironment() &&
          toast.error("Insurance Expiring With in 30 days api Failed", {
            ...commonToast,
          });
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerOneTimeScheduledPayments = createAsyncThunk(
  "dashboard/fetchBorrowerOneTimeScheduledPayments",
  async ({ userId, headers, signal }, thunkAPI) => {
    try {
      const res = await axios.get(
        urls.borrower.scheduledPayments(userId),
        headers,
        { signal },
      );

      const data = res?.data?.items?.map((el) => ({
        ...el,
        date_issued: moment(el.date_issued).format("MM/DD/YYYY"),
      }));
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching scheduled one time payments:", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const deleteScheduledOneTimePayment = createAsyncThunk(
  "dashboard/deleteScheduledOneTimePayment",
  async ({ achId, headers, refreshData }, thunkAPI) => {
    try {
      const res = await axios.delete(
        urls.borrower.deleteScheduledPayment(achId),
        headers,
      );

      if (res.status == 200) {
        await refreshData();
      }
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error deleting scheduled one time payment:", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchBorrowerOneTimePaymentsHistory = createAsyncThunk(
  "dashboard/fetchBorrowerOneTimePaymentsHistory",
  async ({ userId, headers, signal }, thunkAPI) => {
    try {
      const res = await axios.get(
        urls.borrower.oneTimePaymentsHistory(userId),
        headers,
        { signal },
      );

      const data = res?.data?.items?.map((el) => ({
        ...el,
        date_issued: moment(el.date_issued).format("MM/DD/YYYY"),
      }));
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching one time payments history:", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchPayoffDemands = createAsyncThunk(
  "dashboard/fetchPayoffDemands",
  async ({ email, signal, selectedRole }, thunkAPI) => {
    const url = {
      L: urls.payOffDemand.payOffLoans(email),
      B: urls.payOffDemand.payOffLoansBorrower(email),
    };

    try {
      const res = await axios.get(url[selectedRole], oracleConfig, {
        signal,
      });

      const data = res?.data?.items.map((data) => ({
        ...data,
        created_by: data["lower(xlo.created_by)"],
        last_updated_by: data["lower(xlo.last_updated_by)"],
      }));

      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching payoff demands:", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchPayoffDemandsThirdParty = createAsyncThunk(
  "dashboard/fetchPayoffDemandsThirdParty",
  async ({ email, signal }, thunkAPI) => {
    try {
      const res = await axios.get(
        urls.payOffDemand.thirdPartyPayoffs,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Basic UkVTVEFQSUxPQU5MSU5ROjg4V2VIZWxwVSQjQA==",
            email: email
          },
        },
        {
          signal,
        },
      );

      const data = res?.data?.items;

      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching payoff demands:", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchPayoffDemandsDetail = createAsyncThunk(
  "dashboard/fetchPayoffDemandsDetail",
  async ({ payoffId, email, accountId, signal }, thunkAPI) => {
    try {
      const res = await axios.get(
        urls.payOffDemand.payOffDetail(payoffId),
        oracleConfig,
        {
          signal,
        },
      );
      if (res.status == 200) {
        const res2 = await axios.get(
          // urls.dosssdoc.getMyContribution(email, payoffId),
          urls.payOffDemand.getMyContributionV2(email, payoffId, accountId),
          oracleConfig,
          {
            signal,
          },
        );

        const myContribution = res2?.data?.items[0];

        if (res?.data?.items[0]?.payoff_details) {
          const data = JSON.parse(res?.data?.items[0]?.payoff_details);
          const updatedData = {
            ...data,
            myContribution,
          };
          return updatedData;
        }
        return res?.data?.items[0];
      }
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching payoff demands:", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const updatePayoffDemandsDetail = async (payload, signal) => {
  try {
    const res = await axios.post(
      urls.payOffDemand.updateStatusFlag(),
      payload,
      oracleConfig,
    );

    return res;
  } catch (error) {
    if (error.name === "CanceledError") {
      console.error("Fetch request canceled", error.message);
    } else {
      console.error("Error fetching payoff demands:", error);
    }
  }
};

export const fetchBorrowerOtherDetails = createAsyncThunk(
  "dashboard/fetchBorrowerOtherDetails",
  async ({ loanID, signal }, thunkAPI) => {
    try {
      const res = await axios.get(urls.borrower.otherDetails(loanID), {
        signal,
      });

      const data = res?.data?.[0];
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching borrower other details:", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchVOMRequests = createAsyncThunk(
  "dashboard/fetchVOMRequests",
  async ({ email }, thunkAPI) => {
    try {
      const res = await axios.get(
        urls.VOM.fetchVOMRequests(email),
        oracleConfig,
      );
      const data = res?.data?.items;
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching VOM requests", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchVOMRequestDetails = createAsyncThunk(
  "dashboard/fetchVOMRequestDetails",
  async ({ requestId }, thunkAPI) => {
    try {
      const res = await axios.get(
        urls.VOM.fetchVOMRequestDetails(requestId),
        oracleConfig,
      );
      const data = res?.data?.items;
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching VOM request details", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchUSStates = createAsyncThunk(
  "dashboard/fetchUSStates",
  async (thunkAPI) => {
    try {
      const res = await axios.get(urls.VOM.fetchUSStates, oracleConfig);
      const data = res?.data?.items;
      const options = data?.map((el) => ({
        label: el?.display_value,
        value: el?.return_value,
      }));
      return options;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching US states", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchVOMRequestsForBorrower = createAsyncThunk(
  "dashboard/fetchVOMRequestsForBorrower",
  async ({ payload }, thunkAPI) => {
    try {
      const res = await axios.post(
        urls.VOM.fetchBorrowerVOMRequests,
        payload,
        oracleConfig,
      );
      const data = res?.data?.data;
      return data;
    } catch (error) {
      if (error.name === "CanceledError") {
        console.error("Fetch request canceled", error.message);
      } else {
        console.error("Error fetching VOM requests", error);
      }
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
